import { useAuth } from '@shared/auth';
import { useDisclosure } from '@shared/hooks';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useAppMenu, type AppMenu, type AppMenuConfig } from '../../domains';

interface UserProfile {
  displayName: string;
}

interface SideBarContextProps {
  user: UserProfile;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  isSecondarySideBarMounted: boolean;
  setIsSecondarySideBarMounted: React.Dispatch<React.SetStateAction<boolean>>;
  appMenu: AppMenu[];
}

const SideBarContext = createContext<SideBarContextProps>({
  user: { displayName: '' },
  isOpen: true,
  open: () => {
    // open SideBar function
  },
  close: () => {
    // close SideBar function
  },
  toggle: () => {
    // toggle SideBar function
  },
  isSecondarySideBarMounted: false,
  setIsSecondarySideBarMounted: () => {
    // set status "after" show/hide secondary sidebar
  },
  appMenu: [],
});

interface SideBarProviderProps extends PropsWithChildren {
  appShell: AppMenuConfig[];
}

function SideBarProvider(props: Readonly<SideBarProviderProps>) {
  const { appShell, children } = props;

  const { appMenu } = useAppMenu({ appShell });
  const [isOpen, { open, close, toggle }] = useDisclosure(true);
  const [isSecondarySideBarMounted, setIsSecondarySideBarMounted] = useState(false);
  const { userProfile } = useAuth();

  const user = {
    displayName: `${userProfile?.firstName ?? ''} ${userProfile?.lastName ?? ''}`,
  };

  useEffect(() => {
    if (isSecondarySideBarMounted) {
      close();
    } else {
      open();
    }
    // run effects only when secondary sidebar status is changed
  }, [isSecondarySideBarMounted]);

  const sideBarContextValue = useMemo(
    () => ({
      isOpen,
      open,
      close,
      toggle,
      isSecondarySideBarMounted,
      setIsSecondarySideBarMounted,
      appMenu,
      user,
    }),
    [
      isOpen,
      open,
      close,
      toggle,
      isSecondarySideBarMounted,
      setIsSecondarySideBarMounted,
      appMenu,
      user,
    ],
  );

  return <SideBarContext.Provider value={sideBarContextValue}>{children}</SideBarContext.Provider>;
}

const useSideBar = () => {
  const context = useContext(SideBarContext);
  if (context === undefined) {
    throw new Error('useSideBar must be used within SideBarContext');
  }
  return context;
};

export { SideBarProvider, useSideBar };
